import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import ReactMarkdown from "react-markdown";
import { Helmet } from "../components/Head";
import SelectField from "../components/SelectField";
import { fetchCompanyContext, fetchCompanySummary, fetchFullReport, fetchStats, searchCompany } from "../services/api";
import loadEcharts from "../functions/echartsLoader";
import { buildLineOption, safeNum } from "../functions/sectionReportCalculations";
import "./style/analytics.css";
import "./style/companySearch.css";
import { useAuth } from "../context/AuthContext";
import { APP_API_BASE } from "../services/apiConfig";
import { getAuthHeaders } from "../services/authService";

// START_YEAR je konstanta, ale konec bude dynamický
const START_YEAR = 2016;

const INDUSTRY_RENTABILITY_SERIES = [
  { name: "ROA", key: "ROA__median", percent: true },
  { name: "ROE", key: "ROE__median", percent: true },
  { name: "EBITDA marže", key: "EBITDA marže__median", percent: true },
];

const METRIC_COLORS = {
  REVENUE: "#91cc75", // Zelená (Tržby)
  EBITDA: "#5470c6",  // Modrá (EBITDA)
  PROFIT: "#fac858",  // Žlutá (Zisk)
  DEFAULT: "#73c0de"
};

const getMetricColor = (metricName) => {
  if (!metricName) return METRIC_COLORS.DEFAULT;
  const upper = metricName.toUpperCase();
  
  if (upper.includes("TRŽBY") || upper.includes("TRZBY")) return METRIC_COLORS.REVENUE;
  if (upper.includes("EBITDA")) return METRIC_COLORS.EBITDA;
  if (upper.includes("ZISK")) return METRIC_COLORS.PROFIT;
  
  return METRIC_COLORS.DEFAULT;
};

const normalizePercent = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  
  // Убрана проверка (Math.abs(num) <= 1 ? num * 100 : num), 
  // так как она ошибочно превращала 0.307% в 30.7%.
  // Теперь возвращаем число "как есть".
  return num;
};

const safeNumStrict = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeRatio = (value) => {
  const num = safeNumStrict(value);
  if (!Number.isFinite(num)) return null;
  return Math.abs(num) > 1 ? num / 100 : num;
};

const getFirstNumericValue = (row, keys) => {
  if (!row) return null;
  for (const key of keys) {
    const value = safeNumStrict(row[key]);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const buildConnectedLineOption = (years, series, formatter) => {
  const option = buildLineOption(years, series, formatter);
  option.series = option.series.map((item) => ({ ...item, connectNulls: true }));
  return option;
};

const formatMaybeNumber = (value) => (Number.isFinite(value) ? value : "-");
const formatCurrency = (value) => {
  const numeric = safeNumStrict(value);
  return Number.isFinite(numeric) ? `${numeric.toLocaleString("cs-CZ")}` : "-";
};
const formatPercent = (value) => (Number.isFinite(value) ? `${value.toFixed(1)}%` : "-");
const formatMilKcFromThousands = (value) => {
  const numeric = safeNumStrict(value);
  if (!Number.isFinite(numeric)) return "-";
  const milValue = numeric / 1000;
  const absValue = Math.abs(milValue);
  const fractionDigits = absValue < 100 ? 1 : 0;
  const formatted = milValue.toLocaleString("cs-CZ", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
  return `${formatted} mil. Kč`;
};

const CompanySearchPage = () => {
  const HISTORY_PAGE_SIZE = 20;
  const location = useLocation();
  const { reload } = useAuth();
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyError, setHistoryError] = useState("");
  const [query, setQuery] = useState("");
  const [exactMatches, setExactMatches] = useState([]);
  const [fuzzyMatches, setFuzzyMatches] = useState([]);
  const [searchStatus, setSearchStatus] = useState("idle");
  const [icoQuery, setIcoQuery] = useState("");
  const [searchError, setSearchError] = useState("");
  const [contextStatus, setContextStatus] = useState("idle");
  const [contextError, setContextError] = useState("");
  const [contextData, setContextData] = useState(null);
  const [summaryStatus, setSummaryStatus] = useState("idle");
  const [summaryError, setSummaryError] = useState("");
  const [summaryText, setSummaryText] = useState("");
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [loadingIco, setLoadingIco] = useState(null);
  const [industryReport, setIndustryReport] = useState(null);
  const [industryStatus, setIndustryStatus] = useState("idle");

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${APP_API_BASE}/api/company/history`, {
        headers: { ...getAuthHeaders() },
      });
      const history = response.data?.history || [];
      const mapped = history.map((entry, index) => ({
        company: entry.company || "—",
        ico: entry.ico || "",
        time: entry.time ? new Date(entry.time).toLocaleString("cs-CZ") : "",
        status: entry.status || "pending",
        label: entry.label || "Probíhá",
        key: `${entry.ico || "ico"}-${entry.time || index}`,
      }));
      setAnalysisHistory(mapped);
      setHistoryPage(1);
      setHistoryError("");
    } catch (error) {
      console.error(error);
      setHistoryError("Nepodařilo se načíst historii analýz.");
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);
  const historyTotalPages = Math.max(1, Math.ceil(analysisHistory.length / HISTORY_PAGE_SIZE));
  const paginatedHistory = useMemo(() => {
    const startIndex = (historyPage - 1) * HISTORY_PAGE_SIZE;
    return analysisHistory.slice(startIndex, startIndex + HISTORY_PAGE_SIZE);
  }, [analysisHistory, historyPage, HISTORY_PAGE_SIZE]);

  useEffect(() => {
    if (historyPage > historyTotalPages) {
      setHistoryPage(historyTotalPages);
    }
  }, [historyPage, historyTotalPages]);

  const [industryError, setIndustryError] = useState("");
  const [industryModalOpen, setIndustryModalOpen] = useState(false);
  const [structureStatus, setStructureStatus] = useState("idle");
  const [structureError, setStructureError] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [industrySelectionInitialized, setIndustrySelectionInitialized] = useState(false);
  const [manualIndustryId, setManualIndustryId] = useState(null);
  const [manualIndustryLevel, setManualIndustryLevel] = useState(null);
  const [manualIndustryLabel, setManualIndustryLabel] = useState("");
  const [allSections, setAllSections] = useState([]);
  const [allL1, setAllL1] = useState([]);
  const [allL2, setAllL2] = useState([]);
  const [lvl1Options, setLvl1Options] = useState([]);
  const [lvl2Options, setLvl2Options] = useState([]);
  const [selectedSekce, setSelectedSekce] = useState("");
  const [selectedL1, setSelectedL1] = useState("");
  const [selectedL2, setSelectedL2] = useState("");
  const revenueChartRef = useRef(null);
  const profitabilityChartRef = useRef(null);
  const activityIndexRef = useRef(null);
  const industryRentabilityRef = useRef(null);
  const companyAssetsChartRef = useRef(null);
  const industryAssetsChartRef = useRef(null);
  const reportRef = useRef(null);
  const financialDetailsRef = useRef(null);
  const additionalDetailsRef = useRef(null);

  const financials = useMemo(() => {
    if (!contextData?.financni_ukazatele) return [];
    return [...contextData.financni_ukazatele].sort((a, b) => a.Rok - b.Rok);
  }, [contextData]);

  const sectionSelectOptions = useMemo(
    () => [
      { value: "", label: "-- Vyberte sekci --" },
      ...allSections.map((s) => ({
        value: s.Sekce,
        label: `${s.Sekce} - ${s["Sekce název"]}`,
      })),
    ],
    [allSections]
  );

  const level1SelectOptions = useMemo(
    () => [
      { value: "", label: "-- Vyberte skupinu --" },
      ...lvl1Options.map((l) => ({
        value: l["1 úroveň"],
        label: `${l["1 úroveň"]} - ${l["1 úroveň název"]}`,
      })),
    ],
    [lvl1Options]
  );

  const level2SelectOptions = useMemo(
    () => [
      { value: "", label: "-- Vyberte podskupinu --" },
      ...lvl2Options.map((l) => ({
        value: l["2 úroveň ID"],
        label: `${l["2 úroveň ID"]} - ${l["2 úroveň název"]}`,
      })),
    ],
    [lvl2Options]
  );

  // --- DYNAMICKÝ VÝPOČET ROKŮ ---
  const years = useMemo(() => {
    let endYear = 2024;
    if (financials.length > 0) {
      const lastDataYear = financials[financials.length - 1].Rok;
      if (lastDataYear > endYear) {
        endYear = lastDataYear;
      }
    }
    return Array.from(
      { length: endYear - START_YEAR + 1 }, 
      (_, index) => START_YEAR + index
    );
  }, [financials]);

  // --- FORMOVÁNÍ POPISKU LET PRO BENCHMARK ---
  const benchYearsLabel = useMemo(() => {
    const yearsArr = contextData?.bench_years;
    if (!yearsArr || !Array.isArray(yearsArr) || yearsArr.length === 0) {
      return "";
    }
    const minYear = Math.min(...yearsArr);
    const maxYear = Math.max(...yearsArr);
    
    if (minYear === maxYear) {
      return `(${minYear})`;
    }
    return `(${minYear}–${maxYear})`;
  }, [contextData]);

  // --- METRIKY PRO SOUHRN ---
  const summaryMetrics = useMemo(() => {
    if (!financials.length) return null;
    const last = financials[financials.length - 1];

    // Načtení analýzy z benchmarku
    const analysis = contextData?.bench_result?.analysis || {};

    // 1. CAGR (z benchmarku)
    const cagrKey = Object.keys(analysis).find(key => 
      key.toLowerCase().includes("trzby_cagr") || key === "trzby_cagr"
    );
    const revenueCagr = cagrKey ? analysis[cagrKey].company_value : null;

    // 2. ROA (z benchmarku)
    const roaKey = Object.keys(analysis).find(key => 
      key.toLowerCase().includes("roa")
    );
    const roa = roaKey ? normalizePercent(analysis[roaKey].company_value) : null;

    // 3. ROE (z benchmarku)
    const roeKey = Object.keys(analysis).find(key => 
      key.toLowerCase().includes("roe")
    );
    const roe = roeKey ? normalizePercent(analysis[roeKey].company_value) : null;

    const ebitdaMargin = normalizePercent(last["EBITDA marže (%)"]);

    return {
      latestYear: last.Rok,
      revenueCagr,
      ebitdaMargin,
      roa,
      roe,
      revenueLatest: safeNum(last["Tržby"]),
      ebitdaLatest: safeNum(last["EBITDA"]),
      profitLatest: safeNum(last["Zisk po zdanění"]),
    };
  }, [financials, contextData]);

  const shouldShowIndustryManualHint =
    industryStatus === "error" ||
    (industryStatus === "done" &&
      !industryReport?.indices?.length &&
      !industryReport?.financial_ratios?.history?.length &&
      !industryReport?.financial_ratios?.period_all?.length);

const valuationRangeText = useMemo(() => {
    const range = contextData?.valuation_result?.estimated_value_range;
    if (!range) return "-";

    // 1. Získáme surová čísla
    const lowRaw = safeNumStrict(range.low);
    const highRaw = safeNumStrict(range.high);

    // 2. Ověříme, zda jsou to čísla
    const hasLow = Number.isFinite(lowRaw);
    const hasHigh = Number.isFinite(highRaw);

    if (!hasLow && !hasHigh) return "-";

    // 3. Vydělíme 1000 a zaokrouhlíme na celé jednotky
    const low = hasLow ? Math.round(lowRaw / 1000) : null;
    const high = hasHigh ? Math.round(highRaw / 1000) : null;

    // 4. Formátování a výstup
    if (hasLow && hasHigh) {
      return `${formatCurrency(low)} – ${formatCurrency(high)} mil. Kč`;
    }

    const singleValue = hasLow ? formatCurrency(low) : formatCurrency(high);
    return `${singleValue} mil. Kč`;
  }, [contextData]);


  const formatBenchmarkValue = (metric, value) => {
    const numeric = safeNumStrict(value);
    if (!Number.isFinite(numeric)) return value ?? "-";

    const lower = metric.toLowerCase();

    // 1. Obrat (Оборот) - всегда целое число (например, 112 168)
    if (lower.includes("obrat")) {
      return numeric.toLocaleString("cs-CZ", { maximumFractionDigits: 0 });
    }

    // 2. Likvidita (Ликвидность) - всегда 2 знака после запятой (например, 2.15)
    if (lower.includes("likvidita")) {
      return numeric.toFixed(2);
    }

    // 3. Проценты (ROA, ROE, Marže, Zadluženost, CAGR)
    // Умножаем на 100 и добавляем знак %
    if (
      lower.includes("roa") ||
      lower.includes("roe") ||
      lower.includes("ros") ||
      lower.includes("marže") ||
      lower.includes("marze") ||
      lower.includes("margin") ||
      lower.includes("zadluženost") ||
      lower.includes("cagr") ||
      lower.includes("%")
    ) {
      return `${(numeric * 100).toFixed(1)}%`;
    }

    // Для всех остальных метрик - стандартный формат
    return numeric.toLocaleString("cs-CZ");
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("cs-CZ");
  };
  const resolveIndustryLevel = (value) => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (/^[a-z]$/i.test(raw)) return "sekce";
    if (raw.includes(".")) return "l2";
    if (/^\d{2}$/.test(raw)) return "l1";
    return "l1";
  };

  const buildIndustryLabel = (level, value) => {
    if (!level || !value) return "";
    if (level === "sekce") {
      const row = allSections.find((s) => String(s.Sekce) === String(value));
      return row ? `${row.Sekce} - ${row["Sekce název"]}` : String(value);
    }
    if (level === "l1") {
      const row = allL1.find((l) => String(l["1 úroveň"]) === String(value));
      return row ? `${row["1 úroveň"]} - ${row["1 úroveň název"]}` : String(value);
    }
    const row = allL2.find((l) => String(l["2 úroveň ID"]) === String(value));
    return row ? `${row["2 úroveň ID"]} - ${row["2 úroveň název"]}` : String(value);
  };

  const normalizePercentile = (value) => {
    const numeric = safeNumStrict(value);
    if (!Number.isFinite(numeric)) return null;
    return Math.abs(numeric) <= 1 ? numeric * 100 : numeric;
  };

  const getPercentileClass = (value) => {
    if (!Number.isFinite(value)) return "";
    if (value >= 75) return "company-search__percentile--high";
    if (value >= 40) return "company-search__percentile--mid";
    return "company-search__percentile--low";
  };

  const benchmarkRows = useMemo(() => {
    const analysis = contextData?.bench_result?.analysis;
    if (!analysis) return [];
    return Object.entries(analysis)
      .map(([metric, info]) => ({
        metric,
        companyValue: info.company_value,
        median: info.median,
        percentile: info.percentile,
        text: info.text,
        status: info.status,
      }))
      .sort((a, b) => {
        const aIsCagr = b.metric?.toLowerCase().includes("cagr");
        const bIsCagr = a.metric?.toLowerCase().includes("cagr");
        if (aIsCagr && !bIsCagr) return -1;
        if (!aIsCagr && bIsCagr) return 1;
        return a.metric.localeCompare(b.metric, "cs");
      });
  }, [contextData]);

  const additionalInfoRows = useMemo(() => {
    if (!contextData?.zakladni_udaje) return [];
    const details = contextData.zakladni_udaje;
    return [
      { label: "Právní forma", value: details.pravni_forma || "-" },
      { label: "Vlastnictví", value: details.vlastnictvi || "-" },
      { label: "Status subjektu", value: details.status || "-" },
      { label: "Adresa", value: details.adresa || "-" },
      { label: "Plátce DPH", value: details.je_platce_dph ? "Ano" : "Ne" },
      { label: "Datum založení", value: formatDate(details.datum_zalozeni) },
      { label: "Datum účetní závěrky", value: formatDate(details.datum_uzaverky) },
      { label: "Zveřejnění závěrky", value: formatDate(details.datum_zverejneni_uzaverky) },
      { label: "Web", value: details.web || "-" },
    ];
  }, [contextData]);

  const financialTableRows = useMemo(() => {
    if (!financials.length) return [];
    return [...financials].sort((a, b) => b.Rok - a.Rok);
  }, [financials]);

  const revenueSeries = useMemo(() => {
    if (!financials.length) return null;
    return {
      years: years,
      series: [
        {
          name: "Tržby",
          itemStyle: { color: METRIC_COLORS.REVENUE },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? safeNumStrict(row["Tržby"]) : null;
          }),
        },
        {
          name: "EBITDA",
          itemStyle: { color: METRIC_COLORS.EBITDA },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? safeNumStrict(row["EBITDA"]) : null;
          }),
        },
        {
          name: "Zisk po zdanění",
          itemStyle: { color: METRIC_COLORS.PROFIT },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? safeNumStrict(row["Zisk po zdanění"]) : null;
          }),
        },
      ],
    };
  }, [financials, years]);

  const profitabilitySeries = useMemo(() => {
    if (!financials.length) return null;
    return {
      years: years,
      series: [
        {
          name: "ROA",
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? normalizePercent(row["ROA (%)"]) : null;
          }),
        },
        {
          name: "ROE",
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? normalizePercent(row["ROE (%)"]) : null;
          }),
        },
        {
          name: "EBITDA marže",
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return row ? normalizePercent(row["EBITDA marže (%)"]) : null;
          }),
        },
      ],
    };
  }, [financials, years]);

  const activityIndexSeries = useMemo(() => {
    // 1. Načtení indexů a kontrola
    const indices = industryReport?.indices;
    if (!indices?.length) return null;

    // 2. Výběr správného časového okna (window)
    const windows = Array.from(new Set(indices.map((row) => row.window)));
    const targetWindow = `${START_YEAR}_${years[years.length - 1]}`;
    const window = windows.includes(targetWindow) 
        ? targetWindow 
        : (windows.includes("2016_2025") ? "2016_2025" : windows[0]);

    const filteredIndices = indices.filter((row) => row.window === window);
    
    // 3. Získání dat pro TRŽBY (jako základ pro výpočet)
    // Hledáme metriku obsahující "TRŽBY" (ideálně "TRŽBY CELKEM")
    const revenueRows = filteredIndices.filter((row) => 
      row.metric.toUpperCase().includes("TRŽBY")
    );

    if (!revenueRows.length) return null;

    // 4. Získání dat pro EBITDA marži (z financial_ratios)
    const history = industryReport?.financial_ratios?.history || [];
    const periodAll = industryReport?.financial_ratios?.period_all?.[0] || null;
    
    // Fallback pokud chybí historie
    const marginRows = history.length
      ? history
      : years.map((year) => ({ Year: year, ...periodAll }));

    const marginKey = "EBITDA marže__median"; 

    // 5. Sestavení sérií
    return { 
      years: years, 
      window: window,
      series: [
        {
          name: "Tržby (index)",
          itemStyle: { color: METRIC_COLORS.REVENUE },
          data: years.map((year) => {
            // Najdeme řádek pro tržby v daném roce
            const row = revenueRows.find((item) => item.Year === year);
            return row ? safeNumStrict(row.index_median) : null;
          }),
        },
        {
          name: "EBITDA (index)",
          itemStyle: { color: METRIC_COLORS.EBITDA },
          data: years.map((year) => {
            // A. Získáme hodnotu Tržeb (Index)
            const revRow = revenueRows.find((item) => item.Year === year);
            const revenue = revRow ? safeNumStrict(revRow.index_median) : null;

            // B. Získáme EBITDA marži (Ratio)
            const marginRow = marginRows.find((item) => item.Year === year);
            const rawMargin = marginRow ? marginRow[marginKey] : null;
            const marginRatio = normalizeRatio(rawMargin); // Převede 15 na 0.15

            if (!Number.isFinite(revenue) || !Number.isFinite(marginRatio)) return null;

            // C. Vracíme objekt:
            // value = Tržby * Marže (vypočítaná absolutní hodnota EBITDA)
            // margin = Procentuální marže pro tooltip
            return {
              value: revenue * marginRatio,
              margin: Number.isFinite(rawMargin) ? rawMargin : marginRatio * 100
            };
          }),
        }
      ] 
    };
  }, [industryReport, years]);

  const industryRentabilitySeries = useMemo(() => {
    const history = industryReport?.financial_ratios?.history || [];
    const periodAll = industryReport?.financial_ratios?.period_all?.[0] || null;
    if (!history.length && !periodAll) return null;
    
    const rows = history.length
      ? history
      : years.map((year) => ({
          Year: year,
          ...periodAll,
        }));
        
    return {
      years: years,
      series: INDUSTRY_RENTABILITY_SERIES.map((def) => ({
        name: def.name,
        data: years.map((year) => {
          const row = rows.find((item) => item.Year === year);
          if (!row) return null;
          
          // OPRAVA: Získání hodnoty a vynásobení 100, pokud jde o procentuální metriku
          const val = def.percent ? normalizePercent(row[def.key]) : safeNum(row[def.key]);
          return (def.percent && val !== null) ? val * 100 : val;
        }),
      })),
    };
  }, [industryReport, years]);

  const companyAssetsDebtSeries = useMemo(() => {
    if (!financials.length) return null;
    const assetKeys = ["Aktiva celkem", "Aktiva", "Celková aktiva", "AKTIVA CELKEM"];
    const debtKeys = ["Zadluženost celková (%)"];

    return {
      years,
      series: [
        {
          name: "Aktiva",
          itemStyle: { color: METRIC_COLORS.REVENUE },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            return getFirstNumericValue(row, assetKeys);
          }),
        },
        {
          name: "Cizí zdroje",
          itemStyle: { color: METRIC_COLORS.PROFIT },
          data: years.map((year) => {
            const row = financials.find((item) => item.Rok === year);
            if (!row) return null;
            
            const assets = getFirstNumericValue(row, assetKeys);
            const rawRatio = getFirstNumericValue(row, debtKeys); // Např. 45.5
            const ratio = normalizeRatio(rawRatio); // Např. 0.455

            if (!Number.isFinite(assets) || !Number.isFinite(ratio)) return null;
            
            // Vracíme objekt: value pro graf, ratio pro tooltip
            return {
              value: assets * ratio,      // Absolutní hodnota (Kč)
              ratio: Number.isFinite(rawRatio) ? rawRatio : ratio * 100 // Procenta
            };
          }),
        },
      ],
    };
  }, [financials, years]);

  const industryAssetsDebtSeries = useMemo(() => {
    const indices = industryReport?.indices || [];
    if (!indices.length) return null;

    const windows = Array.from(new Set(indices.map((row) => row.window)));
    const targetWindow = `${START_YEAR}_${years[years.length - 1]}`;
    const window = windows.includes(targetWindow)
      ? targetWindow
      : windows.includes("2016_2025")
      ? "2016_2025"
      : windows[0];

    const filtered = indices.filter((row) => row.window === window);
    if (!filtered.length) return null;

    const assetsRows = filtered.filter((row) => row.metric?.toUpperCase().includes("AKTIVA CELKEM"));
    if (!assetsRows.length) return null;

    const history = industryReport?.financial_ratios?.history || [];
    const periodAll = industryReport?.financial_ratios?.period_all?.[0] || null;
    const ratioRows = history.length
      ? history
      : years.map((year) => ({
          Year: year,
          ...periodAll,
        }));

    // Přidáme klíč s procenty pro jistotu
    const ratioKeys = ["Zadluženost celková__median", "Zadluženost celková", "Zadluzenost celkova", "Zadluženost celková (%)"];

    return {
      years,
      series: [
        {
          name: "Aktiva celkem (index)",
          itemStyle: { color: METRIC_COLORS.REVENUE },
          data: years.map((year) => {
            const row = assetsRows.find((item) => item.Year === year);
            return row ? safeNumStrict(row.index_median) : null;
          }),
        },
        {
          name: "Zadluženost celková (přepočet)",
          itemStyle: { color: METRIC_COLORS.PROFIT },
          data: years.map((year) => {
            const assetRow = assetsRows.find((item) => item.Year === year);
            const assets = assetRow ? safeNumStrict(assetRow.index_median) : null;
            
            const ratioRow = ratioRows.find((item) => item.Year === year);
            // Získáme surové číslo (např. 0.15 nebo 15)
            const rawRatio = getFirstNumericValue(ratioRow, ratioKeys);
            // Normalizujeme pro výpočet (např. 0.15)
            const ratio = normalizeRatio(rawRatio);

            if (!Number.isFinite(assets) || !Number.isFinite(ratio)) return null;

            // TRIK: Vracíme objekt místo čísla
            return {
              value: assets * ratio,       // 1. Hodnota pro osu Y (křivka) = Index * Poměr
              realValue: ratio * 100       // 2. Skrytá hodnota pro Tooltip = Reálná procenta
            };
          }),
        },
      ],
      window,
    };
  }, [industryReport, years]);


  const executeSearchQuery = async (searchQuery) => {
    const trimmed = String(searchQuery || "").trim();
    if (!trimmed) return [];

    setSearchStatus("loading");
    setSearchError("");
    setExactMatches([]);
    setFuzzyMatches([]);
    setContextData(null);
    setSummaryText("");
    setSummaryStatus("idle");
    setSummaryError("");
    setSelectedCompany(null);
    setManualIndustryId(null);
    setManualIndustryLevel(null);
    setManualIndustryLabel("");

    const exact = await searchCompany(trimmed, "exact");
    if (exact.length > 0) {
      setExactMatches(exact);
      setSearchStatus("done");
      return exact;
    }

    const fuzzy = await searchCompany(trimmed, "fuzzy");
    setFuzzyMatches(fuzzy);
    setSearchStatus("done");
    return fuzzy;
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    if (!query.trim()) {
      setSearchError("Zadejte název firmy.");
      return;
    }

    try {
      await executeSearchQuery(query);
    } catch (err) {
      setSearchError(err.message || "Chyba při hledání.");
      setSearchStatus("error");
    }
  };

  const loadCompanyData = async (ico, rawOborId = null) => {
    if (!ico) {
      setContextError("IČO není dostupné pro vybranou firmu.");
      setContextStatus("error");
      return;
    }

    setContextStatus("loading");
    setContextError("");
    setSummaryStatus("loading");
    setSummaryError("");
    setSummaryText("");
    setLoadingIco(ico);
    let loadedContext = null;
    try {
      const [contextResult, summaryResult] = await Promise.allSettled([
        fetchCompanyContext(ico, rawOborId),
        fetchCompanySummary(ico, rawOborId),
      ]);
      if (contextResult.status === "fulfilled") {
        const data = contextResult.value;
        loadedContext = data;
        setContextData(data);
        setContextStatus("done");
        await reload();
        await fetchHistory();
      } else {
        setContextData(null);
        const errorMessage = contextResult.reason?.message || "Chyba při načítání kontextu.";
        setContextError(errorMessage === "Insufficient credits" ? "Nedostatek kreditů (potřeba 1 kredit)." : errorMessage);
        setContextStatus("error");
      }

      if (summaryResult.status === "fulfilled") {
        setSummaryText(summaryResult.value?.summary || "");
        setSummaryStatus("done");
      } else {
        setSummaryText("");
        setSummaryError(summaryResult.reason?.message || "Chyba při načítání souhrnu.");
        setSummaryStatus("error");
      }
    } catch (err) {
      setContextError(err.message || "Chyba při načítání kontextu.");
      setContextStatus("error");
    } finally {
      setLoadingIco(null);
    }
    return loadedContext;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const initialQuery = (params.get("query") || "").trim();
    const shouldAnalyze = params.get("analyze") === "1";
    const shouldSearch = params.get("search") === "1";

    if (!initialQuery) return;

    const isIco = /^\d{8}$/.test(initialQuery);
    const hasLetters = /[A-Za-zÀ-ž]/.test(initialQuery);

    if (isIco) {
      setIcoQuery(initialQuery);
      setQuery("");
    } else {
      setQuery(initialQuery);
      setIcoQuery("");
    }

    const runAutoFlow = async () => {
      try {
        if (isIco && shouldAnalyze) {
          const data = await loadCompanyData(initialQuery);
          if (data) {
            setSelectedCompany({
              "Subject name": data?.zakladni_udaje?.nazev || "Zadané IČO",
              "Reg. no.": initialQuery,
            });
          }
          return;
        }

        if ((shouldSearch || hasLetters) && !isIco) {
          await executeSearchQuery(initialQuery);
        }
      } catch (err) {
        setSearchError(err.message || "Chyba při automatickém načtení vyhledání.");
        setSearchStatus("error");
      }
    };

    runAutoFlow();
  }, [location.search]);

  const handleDirectIco = async (event) => {
    event.preventDefault();
    const ico = icoQuery.trim();
    if (!ico) {
      setContextError("Zadejte IČO firmy.");
      setContextStatus("error");
      return;
    }

    setSelectedCompany(null);
    setManualIndustryId(null);
    setManualIndustryLevel(null);
    setManualIndustryLabel("");
    const data = await loadCompanyData(ico);
    if (data) {
      setSelectedCompany({
        "Subject name": data?.zakladni_udaje?.nazev || "Zadané IČO",
        "Reg. no.": ico,
      });
    }
  };

  const handleHistoryAnalyze = async (ico, company) => {
    if (!ico) return;
    setIcoQuery(ico);
    setSelectedCompany({
      "Subject name": company || "Zadané IČO",
      "Reg. no.": ico,
    });
    setManualIndustryId(null);
    setManualIndustryLevel(null);
    setManualIndustryLabel("");
    await loadCompanyData(ico);
  };

  const handleLoadContext = async (row) => {
    const ico = row["Reg. no."] || row.ico || row.IČO;
    if (!ico) {
      setContextError("IČO není dostupné pro vybranou firmu.");
      setContextStatus("error");
      return;
    }

    setSelectedCompany(row);
    setManualIndustryId(null);
    setManualIndustryLevel(null);
    setManualIndustryLabel("");
    await loadCompanyData(ico);
  };

  useEffect(() => {
    const oborMeta = contextData?.obor_meta;
    if (!oborMeta?.level || !oborMeta?.id) {
      setIndustryReport(null);
      setIndustryStatus("idle");
      setIndustryError("");
      return;
    }

    setIndustryStatus("loading");
    setIndustryError("");
    fetchFullReport(oborMeta.level, oborMeta.id)
      .then((data) => {
        setIndustryReport(data);
        setIndustryStatus("done");
        if (!data?.financial_ratios?.history?.length && !data?.financial_ratios?.period_all?.length) {
          console.warn("Industry report missing time series and period data", { oborMeta, data });
        } else if (!data?.financial_ratios?.history?.length && data?.financial_ratios?.period_all?.length) {
          console.info("Industry report uses period_all fallback for comparison charts", { oborMeta });
        }
      })
      .catch((err) => {
        setIndustryReport(null);
        setIndustryStatus("error");
        setIndustryError(err.message || "Chyba při načítání oborových dat.");
        console.error("Industry report fetch failed", { oborMeta, error: err });
      });
  }, [contextData?.obor_meta?.level, contextData?.obor_meta?.id]);

  useEffect(() => {
    if (!industryModalOpen) {
      setIndustrySelectionInitialized(false);
      return;
    }
    if (structureStatus === "loading" || structureStatus === "done") return;
    setStructureStatus("loading");
    setStructureError("");
    Promise.all([fetchStats("sections", "all"), fetchStats("level1", "all"), fetchStats("level2", "all")])
      .then(([sections, level1, level2]) => {
        setAllSections(sections);
        setAllL1(level1);
        setAllL2(level2);
        setStructureStatus("done");
      })
      .catch((err) => {
        console.error("Failed to load industry structure", err);
        setStructureError(err.message || "Chyba při načítání oborů.");
        setStructureStatus("error");
      });
  }, [industryModalOpen, structureStatus]);

  useEffect(() => {
    if (!selectedSekce) {
      setLvl1Options([]);
      if (industrySelectionInitialized) {
        setSelectedL1("");
        setSelectedL2("");
      }
      return;
    }
    const filteredL1 = allL1.filter((row) => row.Sekce === selectedSekce);
    filteredL1.sort((a, b) => a["1 úroveň"] - b["1 úroveň"]);
    setLvl1Options(filteredL1);
    if (industrySelectionInitialized) {
      setSelectedL1("");
      setSelectedL2("");
    }
  }, [selectedSekce, allL1, industrySelectionInitialized]);

  useEffect(() => {
    if (!selectedL1) {
      setLvl2Options([]);
      if (industrySelectionInitialized) {
        setSelectedL2("");
      }
      return;
    }
    const filteredL2 = allL2.filter(
      (row) => row.Sekce === selectedSekce && String(row["1 úroveň"]) === String(selectedL1)
    );
    filteredL2.sort((a, b) => String(a["2 úroveň ID"]).localeCompare(String(b["2 úroveň ID"])));
    setLvl2Options(filteredL2);
    if (industrySelectionInitialized) {
      setSelectedL2("");
    }
  }, [selectedL1, allL2, selectedSekce, industrySelectionInitialized]);

  useEffect(() => {
    if (!industryModalOpen || structureStatus !== "done" || industrySelectionInitialized) return;
    const fallbackLevel = manualIndustryLevel || resolveIndustryLevel(manualIndustryId);
    const metaLevel = contextData?.obor_meta?.level;
    const metaId = contextData?.obor_meta?.id;
    const level = fallbackLevel || metaLevel;
    const value = manualIndustryId || metaId;

    if (level === "sekce") {
      setSelectedSekce(value || "");
      setSelectedL1("");
      setSelectedL2("");
    } else if (level === "l1") {
      const row = allL1.find((l1) => String(l1["1 úroveň"]) === String(value));
      setSelectedSekce(row?.Sekce || "");
      setSelectedL1(value || "");
      setSelectedL2("");
    } else if (level === "l2") {
      const row = allL2.find((l2) => String(l2["2 úroveň ID"]) === String(value));
      setSelectedSekce(row?.Sekce || "");
      setSelectedL1(row?.["1 úroveň"] ? String(row["1 úroveň"]) : "");
      setSelectedL2(value || "");
    }

    setIndustrySelectionInitialized(true);
  }, [
    industryModalOpen,
    structureStatus,
    industrySelectionInitialized,
    manualIndustryId,
    manualIndustryLevel,
    contextData?.obor_meta?.level,
    contextData?.obor_meta?.id,
    allL1,
    allL2,
  ]);

  const handleApplyIndustry = () => {
    const rawId = selectedL2 || selectedL1 || selectedSekce;
    if (!rawId) return;
    const level = selectedL2 ? "l2" : selectedL1 ? "l1" : "sekce";
    const label = buildIndustryLabel(level, rawId);
    const ico =
      contextData?.zakladni_udaje?.ico ||
      contextData?.zakladni_udaje?.ico_str ||
      selectedCompany?.["Reg. no."] ||
      selectedCompany?.ico ||
      selectedCompany?.IČO;
    if (!ico) {
      setContextError("IČO není dostupné pro přepočet oboru.");
      setContextStatus("error");
      return;
    }
    setManualIndustryId(rawId);
    setManualIndustryLevel(level);
    setManualIndustryLabel(label);
    setIndustryModalOpen(false);
    loadCompanyData(ico, rawId);
  };

  const handleResetIndustry = () => {
    if (!manualIndustryId) return;
    const ico =
      contextData?.zakladni_udaje?.ico ||
      contextData?.zakladni_udaje?.ico_str ||
      selectedCompany?.["Reg. no."] ||
      selectedCompany?.ico ||
      selectedCompany?.IČO;
    if (!ico) {
      setContextError("IČO není dostupné pro přepočet oboru.");
      setContextStatus("error");
      return;
    }
    setManualIndustryId(null);
    setManualIndustryLevel(null);
    setManualIndustryLabel("");
    loadCompanyData(ico);
  };

  const renderTable = (rows) => (
    <div className="company-search__table-wrapper">
      <table className="company-search__table">
        <thead>
          <tr>
            <th>Název</th>
            <th>IČO</th>
            <th>Sekce</th>
            <th>1 úroveň</th>
            <th>2 úroveň</th>
            <th>Skóre</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const ico = row["Reg. no."] || row.ico || row.IČO;
            const isLoading = contextStatus === "loading" && loadingIco === ico;
            return (
              <tr key={`${row["Reg. no."]}-${index}`}>
                <td>{row["Subject name"]}</td>
                <td>{row["Reg. no."]}</td>
                <td>{row.Sekce}</td>
                <td>{row["1 úroveň"]}</td>
                <td>{row["2 úroveň"]}</td>
                <td>{row.match_score ? `${row.match_score}%` : "100%"}</td>
                <td>
                  <button
                    type="button"
                    className="company-search__action"
                    onClick={() => handleLoadContext(row)}
                    disabled={isLoading}
                  >
                    {isLoading ? "Načítám..." : "Načíst data"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  useEffect(() => {
    if (!financials.length && !industryReport) return;
    let charts = [];
    let handleResize;
    loadEcharts()
      .then((echarts) => {
        const renderChart = (ref, option) => {
          if (!ref.current || !option) return;
          const chart = echarts.getInstanceByDom(ref.current) || echarts.init(ref.current);
          chart.setOption(option);
          charts.push(chart);
        };

        if (revenueSeries) {
          renderChart(
            revenueChartRef,
            buildConnectedLineOption(revenueSeries.years, revenueSeries.series, (value) =>
              Number.isFinite(value) ? value.toLocaleString("cs-CZ") : "—"
            )
          );
        }

        if (profitabilitySeries) {
          renderChart(
            profitabilityChartRef,
            buildConnectedLineOption(profitabilitySeries.years, profitabilitySeries.series, (value) =>
              Number.isFinite(value) ? `${value.toFixed(1)}%` : "—"
            )
          );
        }

        if (activityIndexSeries) {
          const option = buildConnectedLineOption(
            activityIndexSeries.years, 
            activityIndexSeries.series, 
            (value) => Number.isFinite(value) ? value.toLocaleString("cs-CZ") : "—"
          );

          // OPRAVENÝ TOOLTIP FORMATTER
          option.tooltip.formatter = (params) => {
            let result = `<strong>${params[0].axisValue}</strong><br/>`;
            
            params.forEach((param) => {
              const marker = param.marker;
              const seriesName = param.seriesName;
              const data = param.data;
              
              // Získání hodnoty (číslo nebo .value)
              const val = (data && typeof data === 'object' && 'value' in data) ? data.value : data;
              
              // LOGIKA: Pokud je číslo malé (např. index < 100), zobrazíme 3 desetinná místa.
              // Pokud je velké (např. tržby v Kč), zobrazíme bez desetinných míst.
              const isSmallNumber = Number.isFinite(val) && Math.abs(val) < 100;

              let displayStr = Number.isFinite(val) 
                ? val.toLocaleString("cs-CZ", { 
                    minimumFractionDigits: isSmallNumber ? 2 : 0,
                    maximumFractionDigits: isSmallNumber ? 2 : 0 
                  }) 
                : "-";



              result += `${marker} ${seriesName}: <strong>${displayStr}</strong><br/>`;
            });
            return result;
          };

          renderChart(activityIndexRef, option);
        }

        if (industryRentabilitySeries) {
          renderChart(
            industryRentabilityRef,
            buildConnectedLineOption(industryRentabilitySeries.years, industryRentabilitySeries.series, (value) =>
              Number.isFinite(value) ? `${value.toFixed(1)}%` : "—"
            )
          );
        }

        if (companyAssetsDebtSeries) {
          const option = buildConnectedLineOption(
            companyAssetsDebtSeries.years,
            companyAssetsDebtSeries.series,
            (value) => Number.isFinite(value) ? value.toLocaleString("cs-CZ") : "—"
          );

          // PŘIDÁME CUSTOM TOOLTIP FORMATTER
          option.tooltip.formatter = (params) => {
            let result = `<strong>${params[0].axisValue}</strong><br/>`;
            
            params.forEach((param) => {
              const marker = param.marker;
              const seriesName = param.seriesName;
              const data = param.data; 
              
              // Získání hlavní hodnoty (buď přímo číslo, nebo .value z objektu)
              const val = (data && typeof data === 'object' && 'value' in data) ? data.value : data;
              
              // Formátování měny (např. 12 345)
              let displayStr = Number.isFinite(val) 
                ? val.toLocaleString("cs-CZ", { maximumFractionDigits: 0 }) 
                : "-";

              // Pokud máme v datech schované "ratio", přidáme ho do závorky
              if (data && typeof data === 'object' && 'ratio' in data && Number.isFinite(data.ratio)) {
                displayStr += ` (${data.ratio.toFixed(1)} %)`;
              }

              result += `${marker} ${seriesName}: <strong>${displayStr}</strong><br/>`;
            });
            return result;
          };

          renderChart(companyAssetsChartRef, option);
        }

        if (industryAssetsDebtSeries) {
          // Vytvoříme základní option
          const option = buildConnectedLineOption(
            industryAssetsDebtSeries.years,
            industryAssetsDebtSeries.series,
            (value) => Number.isFinite(value) ? value.toLocaleString("cs-CZ") : "—"
          );

          // PŘEPÍŠEME TOOLTIP FORMATTER
          // Abychom zobrazili "realValue" pokud existuje
          option.tooltip.formatter = (params) => {
            // params je pole bodů pod kurzorem (protože trigger: 'axis')
            let result = `<strong>${params[0].axisValue}</strong><br/>`;
            
            params.forEach((param) => {
              const marker = param.marker;
              const seriesName = param.seriesName;
              const data = param.data; // Zde je naše číslo NEBO náš objekt {value, realValue}

              let displayValue;
              
              // Detekce: Je to objekt a má naši skrytou proměnnou?
              if (data && typeof data === 'object' && 'realValue' in data) {
                // Zobrazíme reálná procenta
                displayValue = `${data.realValue.toFixed(1)} %`;
                // Volitelně můžete přidat info, že jde o přepočet, např:
                // displayValue = `${data.realValue.toFixed(1)} % (index: ${data.value.toFixed(0)})`;
              } else {
                // Standardní zobrazení pro Aktiva (nebo pokud je data jen číslo)
                const val = (data && typeof data === 'object') ? data.value : data;
                displayValue = Number.isFinite(val) ? val.toLocaleString("cs-CZ") : "-";
              }

              result += `${marker} ${seriesName}: <strong>${displayValue}</strong><br/>`;
            });
            return result;
          };

          renderChart(industryAssetsChartRef, option);
        }

        handleResize = () => {
          charts.forEach((chart) => chart.resize());
        };
        window.addEventListener("resize", handleResize);
      })
      .catch((err) => {
        console.error("ECharts load failed", err);
      });

    return () => {
      charts.forEach((chart) => chart.dispose());
      if (handleResize) window.removeEventListener("resize", handleResize);
    };
  }, [
    financials,
    industryReport,
    profitabilitySeries,
    revenueSeries,
    activityIndexSeries,
    industryRentabilitySeries,
    companyAssetsDebtSeries,
    industryAssetsDebtSeries,
  ]);

  const prepareChartsForExport = () => {
    const chartRefs = [
      revenueChartRef,
      activityIndexRef,
      profitabilityChartRef,
      industryRentabilityRef,
      companyAssetsChartRef,
      industryAssetsChartRef,
    ];

    const cleanup = [];

    chartRefs.forEach((ref) => {
      if (!ref.current || !window.echarts) return;
      
      // Получаем инстанс графика
      const chart = window.echarts.getInstanceByDom(ref.current);
      if (!chart) return;

      // 1. Фиксируем размеры
      const width = ref.current.clientWidth;
      const height = ref.current.clientHeight;

      // 2. Генерируем картинку
      const dataUrl = chart.getDataURL({ 
        pixelRatio: 2, 
        backgroundColor: "#ffffff",
        excludeComponents: ['toolbox']
      });

      const img = document.createElement("img");
      img.src = dataUrl;
      img.style.width = `${width}px`;
      img.style.height = `${height}px`;
      img.style.display = "block";
      img.style.objectFit = "contain"; 
      img.dataset.exportChart = "true";

      // 3. Скрываем оригинальный canvas, сохраняя оригинальные стили
      const children = Array.from(ref.current.children);
      children.forEach((child) => {
        // Сохраняем оригинальные значения стилей, чтобы не стереть то, что задал ECharts
        child.dataset.originalVisibility = child.style.visibility;
        child.dataset.originalPosition = child.style.position;
        child.dataset.originalTop = child.style.top;
        child.dataset.originalLeft = child.style.left;

        child.style.visibility = "hidden";
        child.style.position = "absolute";
        child.style.top = "0";
        child.style.left = "0";
      });

      ref.current.style.position = "relative";
      ref.current.appendChild(img);

      // 4. CLEANUP (Восстановление)
      cleanup.push(() => {
        img.remove();
        ref.current.style.position = ""; // Сбрасываем relative

        children.forEach((child) => {
          // Восстанавливаем сохраненные стили, а не просто удаляем их
          child.style.visibility = child.dataset.originalVisibility || "";
          child.style.position = child.dataset.originalPosition || "";
          child.style.top = child.dataset.originalTop || "";
          child.style.left = child.dataset.originalLeft || "";

          // Чистим dataset
          delete child.dataset.originalVisibility;
          delete child.dataset.originalPosition;
          delete child.dataset.originalTop;
          delete child.dataset.originalLeft;
        });

        // --- ГЛАВНОЕ ИСПРАВЛЕНИЕ ---
        // Принудительно просим график пересчитать размеры и перерисоваться
        // requestAnimationFrame гарантирует, что это произойдет в следующем кадре отрисовки
        requestAnimationFrame(() => {
           chart.resize();
        });
      });
    });

    return () => cleanup.forEach((fn) => fn());
  };

  const toggleDetailsForExport = async (open) => {
    const details = reportRef.current.querySelectorAll("details");
    const previousState = [];
    
    details.forEach((detail) => {
      previousState.push({ el: detail, wasOpen: detail.open });
      detail.open = open;
    });

    // Ждем перерисовки браузера
    await new Promise((resolve) => setTimeout(resolve, 100));

    return () => {
      previousState.forEach(({ el, wasOpen }) => {
        el.open = wasOpen;
      });
    };
  };

  const handleDownloadPdf = async () => {
    if (!reportRef.current || isExporting) return;
    setIsExporting(true);
    

    // 1. Открываем все детали и подменяем графики на картинки
    const restoreDetails = await toggleDetailsForExport(true);
    const restoreCharts = prepareChartsForExport();

    try {
      // Инициализация PDF
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 32;
      const contentWidth = pageWidth - margin * 2;
      
      let currentY = margin;

      // Заголовок PDF (метаданные)
      pdf.setProperties({
        title: `Report - ${contextData?.zakladni_udaje?.nazev || "Company"}`,
      });

      // 2. Выбираем логические блоки, которые нельзя разрывать
      // Мы берем прямых детей контейнера отчета, которые являются блоками контента
      const blocks = Array.from(
        reportRef.current.querySelectorAll(
          ".company-search__context-block, .card-grid"
        )
      );

      for (const block of blocks) {
        // Делаем скриншот только конкретного блока
        const canvas = await html2canvas(block, {
          scale: 2, // Качество
          backgroundColor: "#ffffff",
          useCORS: true,
          logging: false,
        });

        const imgData = canvas.toDataURL("image/png");
        const imgProps = pdf.getImageProperties(imgData);
        
        // Вычисляем высоту блока в PDF единицах (pt)
        const pdfImgHeight = (imgProps.height * contentWidth) / imgProps.width;

        // 3. Проверяем, влезает ли блок на текущую страницу
        if (currentY + pdfImgHeight > pageHeight - margin) {
          pdf.addPage();
          currentY = margin; // Сброс позиции на новой странице
        }

        // 4. Рисуем блок
        pdf.addImage(imgData, "PNG", margin, currentY, contentWidth, pdfImgHeight, undefined, "FAST");
        
        // Смещаем курсор вниз + небольшой отступ между блоками
        currentY += pdfImgHeight + 15;
      }

      const ico = contextData?.zakladni_udaje?.ico_str || contextData?.zakladni_udaje?.ico || "firma";
      pdf.save(`report-${ico}.pdf`);

    } catch (error) {
      console.error("PDF download failed", error);
    } finally {
      // Восстанавливаем состояние страницы
      restoreCharts();
      restoreDetails();
      setIsExporting(false);
    }
  };

  return (
    <main className="company-search">
      <Helmet>
        <title>Vyhledání firmy</title>
      </Helmet>

      <section className="company-search__hero">
        <h1>Vyhledání firmy</h1>
        <p>Najděte IČO firmy nebo vyhledejte dle názvu.</p>

          <p>
          Některé firmy nemají dostupné verejné údaje, což může ovlivnit výsledky analýzy.
        </p>
        <p className="company-search__credits-note">Cena analýzy firmy: <strong>2 kredity</strong>.</p>
      </section>

      <form className="company-search__form" onSubmit={handleSearch}>
        <input
          type="text"
          className="company-search__input"
          placeholder="Zadejte název společnosti..."
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <button type="submit" className="company-search__submit" disabled={searchStatus === "loading"}>
          {searchStatus === "loading" ? "Hledám..." : "Hledat"}
        </button>
      </form>
      <form className="company-search__form" onSubmit={handleDirectIco}>
        <input
          type="text"
          className="company-search__input"
          placeholder="Zadejte IČO společnosti..."
          value={icoQuery}
          onChange={(event) => setIcoQuery(event.target.value)}
        />
        <button type="submit" className="company-search__submit" disabled={contextStatus === "loading"}>
          {contextStatus === "loading" ? "Načítám..." : "Načíst podle IČO"}
        </button>
      </form>

      

      {searchError && <p className="company-search__error">{searchError}</p>}

      {exactMatches.length > 0 && (
        <section className="company-search__results">
          <h2>Přesná shoda</h2>
          {renderTable(exactMatches)}
        </section>
      )}

      {exactMatches.length === 0 && searchStatus === "done" && (
        <section className="company-search__results">
          <h2>Top 5 podobných výsledků</h2>
          {fuzzyMatches.length > 0 ? (
            renderTable(fuzzyMatches)
          ) : (
            <p>Žádné výsledky nebyly nalezeny.</p>
          )}
        </section>
      )}

      <section className="company-search__context">
        <h2>Detaily firmy</h2>
        {selectedCompany && (
          <p className="company-search__selected">
            Vybraná firma: <strong>{selectedCompany["Subject name"]}</strong> ({selectedCompany["Reg. no."]})
          </p>
        )}
        {contextStatus === "loading" && (
          <div className="company-search__loading-panel" role="status" aria-live="polite">
            <div className="company-search__loading-spinner" aria-hidden="true" />
            <h3>Firma se načítá, počkejte prosím cca 10–20 vteřin.</h3>
            <p>Právě zpracováváme data firmy a připravujeme analýzu.</p>
            <p className="company-search__loading-note">
              Víte, že můžete upravit automaticky zvolený obor firmy? Zvýšíte tím přesnost ocenění a benchmarku.
            </p>
          </div>
        )}
        {contextError && <p className="company-search__error">{contextError}</p>}
      {contextData && (
        <div className="company-search__context-grid company-search__report" ref={reportRef}>
            <div className="company-search__context-block company-search__context-block--full">
              <div className="company-search__report-header">
                <h3>Souhrn o firmě</h3>
                <button
                  type="button"
                  className="company-search__pdf-button"
                  onClick={handleDownloadPdf}
                  disabled={isExporting}
                >
                  {isExporting ? "Připravuji PDF..." : "Stáhnout PDF"}
                </button>
              </div>
              <div className="company-search__summary">
                <div>
                  <span>Název</span>
                  <strong>{contextData.zakladni_udaje?.nazev || "-"}</strong>
                </div>
                <div>
                  <span>IČO</span>
                  <strong>{contextData.zakladni_udaje?.ico_str || contextData.zakladni_udaje?.ico || "-"}</strong>
                </div>
                <div>
                  <span>Obor</span>
                  <div className="company-search__industry-row">
                    <strong>{manualIndustryLabel || contextData.zakladni_udaje?.obor || "-"}</strong>
                    <button
                      type="button"
                      className="company-search__industry-button"
                      onClick={() => setIndustryModalOpen(true)}
                      disabled={!contextData || contextStatus === "loading"}
                    >
                      Změnit
                    </button>
                  </div>
                  {manualIndustryLabel && (
                    <div className="company-search__industry-note">
                      Ruční obor: {manualIndustryLabel}
                      <button type="button" className="company-search__industry-reset" onClick={handleResetIndustry}>
                        Vrátit automatický
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <span>Zaměstnanci</span>
                  <strong>{contextData.zakladni_udaje?.velikost || "-"}</strong>
                </div>
              </div>
              {summaryMetrics && (
                <div className="company-search__summary-metrics">
                  <div className="company-search__metric-card">
                    <span>Tržby ({summaryMetrics.latestYear})</span>
                    <strong>{formatMilKcFromThousands(summaryMetrics.revenueLatest)}</strong>
                  </div>
                  <div className="company-search__metric-card">
                    <span>EBITDA ({summaryMetrics.latestYear})</span>
                    <strong>{formatMilKcFromThousands(summaryMetrics.ebitdaLatest)}</strong>
                  </div>
                  <div className="company-search__metric-card">
                    <span>Zisk po zdanění</span>
                    <strong>{formatMilKcFromThousands(summaryMetrics.profitLatest)}</strong>
                  </div>
                                    <div className="company-search__metric-card">
                    <span>Růst tržeb(CARG)</span>
                    <strong>
                      {Number.isFinite(summaryMetrics.revenueCagr)
                        ? `${(summaryMetrics.revenueCagr * 100).toFixed(1)}%`
                        : "-"}
                    </strong>
                  </div>
                  <div className="company-search__metric-card company-search__metric-card--wide company-search__metric-card--highlight">
                    <span>Odhad hodnoty společnosti</span>
                    <strong>{valuationRangeText}</strong>
                  </div>
                </div>
              )}
              <div className="company-search__summary-text">
                {summaryStatus === "loading" && <p>Načítám souhrn firmy...</p>}
                {summaryError && <p className="company-search__error">{summaryError}</p>}
                {summaryText && (
                  <div className="company-search__markdown">
                    <ReactMarkdown
                      components={{
                        p: ({ node, ...props }) => <p className="company-search__markdown-paragraph" {...props} />,
                        strong: ({ node, ...props }) => <strong className="company-search__markdown-strong" {...props} />,
                        ul: ({ node, ...props }) => <ul className="company-search__markdown-list" {...props} />,
                        li: ({ node, ...props }) => <li className="company-search__markdown-list-item" {...props} />,
                      }}
                    >
                      {summaryText}
                    </ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
                        <div className="company-search__context-block company-search__context-block--full">
              <details className="company-search__details" ref={additionalDetailsRef}>
                <summary>Doplňující informace</summary>
                {additionalInfoRows.length ? (
                  <div className="company-search__table-wrapper">
                    <table className="company-search__table company-search__table--compact">
                      <tbody>
                        {additionalInfoRows.map((row) => (
                          <tr key={row.label}>
                            <th>{row.label}</th>
                            <td>{row.value}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>Doplňující informace nejsou dostupné.</p>
                )}
              </details>
            </div>

            <div className="company-search__context-block company-search__context-block--full">
              <details className="company-search__details" ref={financialDetailsRef}>
                <summary>Finanční data</summary>
                {financialTableRows.length ? (
                  <div className="company-search__table-wrapper company-search__table-wrapper--wide">
                    <table className="company-search__table company-search__table--compact">
                      <thead>
                        <tr>
                          <th>Rok</th>
                          <th>Tržby</th>
                          <th>EBITDA</th>
                          <th>Zisk po zdanění</th>
                          <th>Aktiva celkem</th>
                          <th>ROA</th>
                          <th>ROE</th>
                          <th>EBITDA marže</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financialTableRows.map((row) => (
                          <tr key={row.Rok}>
                            <td>{row.Rok}</td>
                            <td>{formatCurrency(row["Tržby"])}</td>
                            <td>{formatCurrency(row["EBITDA"])}</td>
                            <td>{formatCurrency(row["Zisk po zdanění"])}</td>
                            <td>{formatCurrency(row["Celková aktiva"])}</td>
                            <td>{formatPercent(normalizePercent(row["ROA (%)"]))}</td>
                            <td>{formatPercent(normalizePercent(row["ROE (%)"]))}</td>
                            <td>{formatPercent(normalizePercent(row["EBITDA marže (%)"]))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>Finanční data nejsou k dispozici.</p>
                )}
              </details>
            </div>

           

            <section className="card-grid company-search__context-block--full">
              <div className="card">
                <div className="card__header">
                  <h4>Vývoj tržeb a zisku firmy (tis. Kč)</h4>
                </div>
                <div className="chart" ref={revenueChartRef} />
              </div>
              <div className="card">
                <div className="card__header">
                  <h4>Indexy medianů odvětví</h4>
                  {activityIndexSeries?.window && <span className="badge badge--ghost">{activityIndexSeries.window}</span>}
                </div>
                <div className="chart" ref={activityIndexRef} />
              </div>
            </section>

            <section className="card-grid company-search__context-block--full">
              <div className="card">
                <div className="card__header">
                  <h4>Rentabilita firmy v čase</h4>
                </div>
                <div className="chart" ref={profitabilityChartRef} />
              </div>
              <div className="card">
                <div className="card__header">
                  <h4>Rentabilita odvětví</h4>
                </div>
                <div className="chart" ref={industryRentabilityRef} />
              </div>
            </section>

            <section className="card-grid company-search__context-block--full">
              <div className="card">
                <div className="card__header">
                  <h4>Aktiva a cizí zdroje firmy (tis. Kč)</h4>
                </div>
                <div className="chart" ref={companyAssetsChartRef} />
              </div>
              <div className="card">
                <div className="card__header">
                  <h4>Aktiva a zadluženost odvětví</h4>
                  {industryAssetsDebtSeries?.window && (
                    <span className="badge badge--ghost">{industryAssetsDebtSeries.window}</span>
                  )}
                </div>
                <div className="chart" ref={industryAssetsChartRef} />
              </div>
            </section>
            {industryStatus === "loading" && <p>Načítám oborová data...</p>}
            {industryError && <p className="company-search__error">{industryError}</p>}
            {industryStatus === "done" &&
              !industryReport?.indices?.length &&
              !industryReport?.financial_ratios?.history?.length &&
              !industryReport?.financial_ratios?.period_all?.length && (
                <p>Oborová data nejsou dostupná.</p>
              )}
            {shouldShowIndustryManualHint && (
              <p className="company-search__industry-help">
                Pokud se obor nenačetl správně, použijte nahoře v panelu tlačítko <strong>Změnit</strong>,
                vyberte obor a zadejte ho ručně.
              </p>
            )}

            

            <div className="company-search__context-block company-search__context-block--full">
              {/* ZDE POUŽIJEME VÝPOČET BENCH YEARS */}
              <h3>Benchmarking {benchYearsLabel}</h3>
              <p> Percentil ukazuje, relevantní pozici firmy v oboru. X% firem v tomto ukazateli má horší vysledky (obor se hodnotil za období 2023-2025).</p>
              {benchmarkRows.length > 0 ? (
                <div className="company-search__table-wrapper">
                  <table className="company-search__table">
                    <thead>
                      <tr>
                        <th>Metrika</th>
                        <th>Firma</th>
                        <th>Medián</th>
                        <th>Percentil</th>
                        <th>Hodnocení</th>
                      </tr>
                    </thead>
                    <tbody>
                      {benchmarkRows.map((row) => {
                        const percentileValue = normalizePercentile(row.percentile);
                        const percentileClass = getPercentileClass(percentileValue);
                        return (
                          <tr key={row.metric} className={percentileClass}>
                            <td>{row.metric}</td>
                            <td>{formatBenchmarkValue(row.metric, row.companyValue)}</td>
                            <td>{formatBenchmarkValue(row.metric, row.median)}</td>
                            <td>
                              {Number.isFinite(percentileValue) ? `${percentileValue.toFixed(1)}%` : row.percentile ?? "-"}
                            </td>
                            <td>{row.text || row.status || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>Data benchmarku nejsou k dispozici.</p>
              )}

              {/* --- METHODOLOGY INFO --- */}
      <div style={{ marginBottom: '1.5rem' }}>
        <details style={{ background: 'white', padding: '1rem', borderRadius: '8px', border: '1px solid #eee' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#027360' }}>ℹ️ Poznámky k metodice výpočtu ukazatelů</summary>
            
            <div style={{ marginTop: '1rem', fontSize: '0.9rem', lineHeight: '1.6', color: '#444' }}>
                <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#0b1f2e' }}>📘 Transkripce výpočtu ukazatelů</h4>
                <p style={{ marginBottom: '1rem' }}>
                    Následující přehled shrnuje, jak byly jednotlivé ukazatele a pomocné položky dopočítány ze základních účetních dat.
                    Výpočty vycházejí z dat firem s ročním obratem nad 30 mil. Kč za období 2016–2025.
                </p>

                <h5 style={{ fontSize: '1rem', marginTop: '1rem', marginBottom: '0.5rem', borderBottom: '2px solid #eee', paddingBottom: '0.25rem' }}>
                    Základní odvozené veličiny
                </h5>
                <ul style={{ paddingLeft: '1.2rem', marginBottom: '1rem' }}>
                    <li>
                        <strong>Tržby celkem</strong> = Tržby za prodej zboží + Tržby za prodej vlastních výrobků a služeb
                    </li>
                    <li>
                        <strong>EBITDA</strong> = EBIT + Odpisy (pokud chybí, dopočteno jako součet EBIT + Odpisy DHM/DNM)
                    </li>
                </ul>

                <h5 style={{ fontSize: '1rem', marginTop: '1rem', marginBottom: '0.5rem', borderBottom: '2px solid #eee', paddingBottom: '0.25rem' }}>
                    Finanční ukazatele
                </h5>
                <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>ROA (Rentabilita aktiv)</strong> = EBIT / Aktiva celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Efektivita využití aktiv pro tvorbu provozního zisku.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>ROE (Rentabilita vlastního kapitálu)</strong> = Výsledek hospodaření <u>před zdaněním</u> / Vlastní kapitál<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Výnosnost pro vlastníky (počítáno ze zisku před zdaněním).</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>ROS (Rentabilita tržeb)</strong> = Výsledek hospodaření <u>před zdaněním</u> / Tržby celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Ziskovost tržeb před zdaněním.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>EBITDA marže</strong> = EBITDA / Tržby celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Provozní výkonnost očištěná o odpisy.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Provozní marže</strong> = EBIT / Tržby celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Efektivita hlavní činnosti podniku.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Zadluženost dlouhodobá</strong> = Dlouhodobé závazky / Aktiva celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Podíl dlouhodobého cizího kapitálu na majetku.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Zadluženost celková</strong> = (Aktiva celkem − Vlastní kapitál) / Aktiva celkem<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Celkový podíl cizích zdrojů financování.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Likvidita běžná</strong> = Oběžná aktiva / Krátkodobé závazky<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Schopnost krýt krátkodobé závazky oběžnými aktivy.</span>
                    </li>
                    <li style={{ marginBottom: '0.5rem' }}>
                        <strong>Likvidita pohotová</strong> = (Peněžní prostředky + Krátkodobé pohledávky + Dlouhodobé pohledávky) / Krátkodobé závazky<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Schopnost platit bez nutnosti prodeje zásob (konzervativní výpočet).</span>
                    </li>
                    <li>
                        <strong>Likvidita okamžitá</strong> = Peněžní prostředky na účtech / Krátkodobé závazky<br/>
                        <span style={{ color: '#666', fontStyle: 'italic' }}>→ Okamžitá schopnost splácet dluhy hotovostí.</span>
                    </li>
                </ul>
            </div>
        </details>
      </div>
            </div>


          </div>
        )}

      {industryModalOpen && (
        <div className="company-search__modal-backdrop">
          <div className="company-search__modal" role="dialog" aria-modal="true">
            <div className="company-search__modal-header">
              <h3>Vyberte obor pro analýzu</h3>
              <button
                type="button"
                className="company-search__modal-close"
                onClick={() => setIndustryModalOpen(false)}
                aria-label="Zavřít"
              >
                ×
              </button>
            </div>
            {structureStatus === "loading" && <p>Načítám seznam oborů...</p>}
            {structureError && <p className="company-search__error">{structureError}</p>}
            {structureStatus === "done" && (
              <div className="company-search__modal-grid">
                <SelectField
                  label="Sekce"
                  value={selectedSekce}
                  onChange={(event) => setSelectedSekce(event.target.value)}
                  options={sectionSelectOptions}
                  selectClassName="company-search__modal-select"
                />
                <SelectField
                  label="1. úroveň"
                  value={selectedL1}
                  onChange={(event) => setSelectedL1(event.target.value)}
                  options={level1SelectOptions}
                  disabled={!selectedSekce}
                  selectClassName="company-search__modal-select"
                />
                <SelectField
                  label="2. úroveň"
                  value={selectedL2}
                  onChange={(event) => setSelectedL2(event.target.value)}
                  options={level2SelectOptions}
                  disabled={!selectedL1}
                  selectClassName="company-search__modal-select"
                />
              </div>
            )}
            <div className="company-search__modal-actions">
              <button
                type="button"
                className="company-search__modal-cancel"
                onClick={() => setIndustryModalOpen(false)}
              >
                Zrušit
              </button>
              <button
                type="button"
                className="company-search__modal-apply"
                onClick={handleApplyIndustry}
                disabled={!(selectedL2 || selectedL1 || selectedSekce) || contextStatus === "loading"}
              >
                Použít obor
              </button>
            </div>
          </div>
        </div>
      )}
      </section>
      <section className="company-search__history">
        <h2>Historie analýz</h2>
        {historyError && <p className="company-search__error">{historyError}</p>}
        <div className="company-search__history-table">
          <div className="company-search__history-row company-search__history-row--header">
            <span>Název firmy</span>
            <span>Čas analýzy</span>
            <span>Stav</span>
            <span>Akce</span>
          </div>
          {paginatedHistory.map((entry) => (
            <div key={entry.key || `${entry.company}-${entry.time}`} className="company-search__history-row">
              <span className="company-search__history-company">{entry.company}</span>
              <span>{entry.time}</span>
              <span className={`company-search__status-badge company-search__status-badge--${entry.status}`}>
                {entry.label}
              </span>
              <span>
                <button
                  type="button"
                  className="company-search__history-action"
                  onClick={() => handleHistoryAnalyze(entry.ico, entry.company)}
                >
                  Analyzovat firmu
                </button>
              </span>
            </div>
          ))}
        </div>
        {historyTotalPages > 1 && (
          <div className="company-search__history-pagination">
            {Array.from({ length: historyTotalPages }, (_, index) => {
              const pageNumber = index + 1;
              return (
                <button
                  key={pageNumber}
                  type="button"
                  className={`company-search__history-page-btn${historyPage === pageNumber ? " is-active" : ""}`}
                  onClick={() => setHistoryPage(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>
        )}
      </section>
    </main>
  );
};

export default CompanySearchPage;
